import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { from, Subject, throwError } from 'rxjs';
import { tap, catchError, takeUntil } from 'rxjs/operators';
import { PayrexxPaymentIntentResponse, FinishPaymentRequest } from 'src/app/api/models';
import { UserPayrexxPaymentsApiService } from 'src/app/api/services';
import { ICustomControl } from 'src/shared/models/form.model';
import { PaymentInfo, CustomerPaymentMethod, PaymentIds, PaymentObject, PaymentMethodEnum } from 'src/shared/models/payment.model';
import { CustomSpinnerService } from 'src/shared/services/helpers/custom-spinner.service';
import { PaymentsHelperService } from 'src/shared/services/helpers/payments-helper.service';
import { MspApiService } from 'src/shared/services/msp-api.service';
import { FormService } from 'src/shared/shared-forms/form.service';
import { PayrexxIframeWrapperComponent } from './components/payrexx-iframe-wrapper/payrexx-iframe-wrapper.component';
import { BookingHelperService } from 'src/shared/services/helpers/booking-helper.service';
import { IonButton } from "@ionic/angular/standalone";
import { TranslatePipe } from '@ngx-translate/core';

@Component({
	selector: 'vl-pay-with-payrexx',
	templateUrl: './pay-with-payrexx.component.html',
	styleUrls: ['./pay-with-payrexx.component.scss'],
	imports: [IonButton, TranslatePipe],
})
export class PayWithPayrexxComponent implements OnInit, OnDestroy/*, PayWithI<PayrexxPaymentIntentResponse>*/ {
	@Input() paymentInfo: PaymentInfo = {} as any;
	@Input() paymentType: { type: 'new-card' | 'saved-card', card: CustomerPaymentMethod };
	// tslint:disable-next-line:no-output-on-prefix
	@Output() onSuccess: EventEmitter<any> = new EventEmitter<any>();
	// tslint:disable-next-line:no-output-on-prefix
	@Output() onError: EventEmitter<any> = new EventEmitter<any>();
	@Output() back: EventEmitter<any> = new EventEmitter<any>();

	public controls: ICustomControl[];
	public submitted = false;
	public isReady = false;
	public isInited = false;
	paymentActive = false;
	paymentIds: PaymentIds;

	private notifier: Subject<any> = new Subject<any>();


	constructor(private _formService: FormService,
				private spinner: CustomSpinnerService,
				private paymentHelper: PaymentsHelperService,
				private payrexxService: UserPayrexxPaymentsApiService,
				private bookingHelper: BookingHelperService,
				private modalController: ModalController,
				private mspApiService: MspApiService) {
	}

	ngOnInit() {
		this.controls = this._formService.paymentControls;
		this.isReady = true;
		this.isInited = true;
	}

	ngOnDestroy(): void {
		this.notifier.next('');
		this.notifier.complete();
	}

	public doPayment() {
		this.spinner.show(false).then(() => {
			this.bookingHelper.towerReadyToAction(this.paymentInfo.type, (valid) => {
				if(valid) {
					this.createPaymentIntent()
						.pipe(
							tap(pi => {
								this.handlePaymentIds(pi);
								this.paymentActive = true;
							}),
							catchError((e) => {
								return this.handlePaymentError(e);
							})
						).subscribe((result) => {
						this.openPayrexxModal(result.paymentIntent.link, this.paymentIds.main);
					});
				}
			}, this.paymentInfo.bookingId, this.paymentInfo.groupId, this.paymentInfo.subscriptionStartDate);
		}
		);
	}

	private async openPayrexxModal(payrexxUrl, paymentId) {
		const modal = await this.modalController.create({
			id: 'payrexx-iframe-wrapper',
			component: PayrexxIframeWrapperComponent,
			componentProps: {
				payrexxUrl,
				paymentId
			}
		});

		from(modal.onWillDismiss())
			.pipe(
				takeUntil(this.notifier)
			)
			.subscribe((v) => {
				console.log('onDIsmiss', v);
				const status: 'COMPLETED' | 'CANCELLED' | 'ERROR' = v.data;
				switch (status) {
					case 'COMPLETED':
						this.handlePaymentSuccess();
						break;
					case 'CANCELLED':
						break;
					case 'ERROR':
						this.onError.emit();
						break;
				}
			});
		modal.present();
	}

	private handlePaymentIds(pi: PayrexxPaymentIntentResponse) {
		this.paymentIds = {
			main: null,
			freeHours: null
		};
		Object.keys(pi.paymentsToMethod).forEach((k: any) => {
			console.log(k, pi.paymentsToMethod[k]);
			if (pi.paymentsToMethod[k] === 'FREE_HOURS') {
				this.paymentIds.freeHours = Number(k);
			} else {
				this.paymentIds.main = Number(k);
			}
		});
	}


	private createPaymentIntent() {
		if (this.paymentInfo.type === PaymentObject.PayPerUse) {
			return this.payrexxService.createOrder({
				body: {
					bookingId: this.paymentInfo.bookingId,
					freeHours: this.paymentInfo.freeHoursUsed
				}
			});
		} else if (this.paymentInfo.type === PaymentObject.Subscription) {
			return this.payrexxService.createSubscriptionPaymentIntent1({
				body: {
					towerGroupId: this.paymentInfo.groupId,
					onDateTime: new Date(this.paymentInfo.subscriptionStartDate).toISOString()
				}
			});
		} else if (this.paymentInfo.type === PaymentObject.Purchase) {
			return this.mspApiService.createPurchasePaymentIntent(this.paymentInfo?.purchase?.id,
				PaymentMethodEnum.PAYREXX_TWINT);
		}
	}

	async handlePaymentSuccess() {
		await this.spinner.show();
		let body: FinishPaymentRequest = {
			paymentId: this.paymentIds.main
		};

		if (this.paymentIds.freeHours) {
			body = {
				...body,
				...{ freeHoursPaymentId: this.paymentIds.freeHours }
			};
		}
		this.payrexxService.captureOrder({
			body
		}).subscribe(() => {
			this.analyticsCompletePayment();
			switch (this.paymentInfo.type) {
				case PaymentObject.PayPerUse:
					this.terminateBooking();
					break;
				case PaymentObject.Subscription:
					this.createSubscription(this.paymentIds.main, this.paymentInfo.subscriptionStartDate);
					break;
				case PaymentObject.Purchase:
					this.finishPurchase();
					break;
			}
		}, error => {
			this.spinner.hide();
			this.onError.emit();
		});
	}

	private terminateBooking() {
		this.paymentHelper.terminateBooking(
			Number(this.paymentInfo.bookingId),
			this.paymentInfo.booking).subscribe(() => {
			// this.paymentActive = false;
			this.onSuccess.emit();
			this.spinner.hide();
		}, error => {
			this.spinner.hide();
			this.onError.emit();
		});
	}

	private createSubscription(paymentId, startDate) {
		this.paymentHelper.createSubscription(
			paymentId,
			this.paymentInfo.groupId,
			startDate
		).subscribe(() => {
			this.onSuccess.emit();
			this.spinner.hide();
		}, error => {
			this.spinner.hide();
			this.onError.emit();
		});
	}

	private analyticsCompletePayment() {
		const dataForAnalytics = { ...this.paymentInfo };
		dataForAnalytics.method = PaymentMethodEnum.STRIPE_CARD;
		// this.analyticsService.logEvent(AnalyticsEvents.CompletePayment, dataForAnalytics);
	}

	private handlePaymentError(e) {
		console.log('error', e);
		this.spinner.hide();
		if ((e.error === 'Internal Server Error' && e.path === '/api/user/payment/subscription') || e.status === 400) {
			this.onError.emit();
		}
		return throwError(e);
	}

	private finishPurchase() {
		this.mspApiService.pingPayment(this.paymentInfo.purchase.id).subscribe(async () => {
			// await this.storageService.setItem(StorageEnums.PurchaseRedirectUrl, null);
			this.onSuccess.emit();
			this.spinner.hide();
		}, error => {
			this.onSuccess.emit();
			this.spinner.hide();
		});
	}

}
